import set from 'lodash/set';

import {
  SYSTEM_CALENDAR_WORKFLOWS,
  SYSTEM_EMAIL_WORKFLOWS,
  SYSTEM_INDIVIDUAL_WORKFLOWS,
} from 'Components/WorkflowsList/utils';
import {WF_ACTION_RECEIVER, WF_METHOD, WF_TYPE} from 'Utils/consts';

const customApptNotifTypeToWfVariant = (apptNotifType) => {
  let serverWfVariant;
  switch (apptNotifType) {
    case WF_TYPE.CALENDAR_INVITE:
      serverWfVariant = WF_TYPE.APPT_ATTENDEE_INVITATION;
      break;
    case WF_TYPE.CONFIRMATION:
      serverWfVariant = WF_TYPE.CONFIRMATION;
      break;
    case WF_TYPE.CANCELLATION:
      serverWfVariant = WF_TYPE.CANCELLATION;
      break;
    case WF_TYPE.APPT_SMS_REMINDER:
    case WF_TYPE.APPT_EMAIL_REMINDER:
      serverWfVariant = WF_TYPE.REMINDER;
      break;
    case WF_TYPE.APPT_EMAIL_FOLLOW_UP:
      serverWfVariant = WF_TYPE.REQUEST_FOLLOW_UP;
      break;
    default:
      console.error('unknown appt notification type', apptNotifType);
      // Pass unknown through
      serverWfVariant = apptNotifType;
      break;
  }
  return serverWfVariant;
};

const customApptNotifTypeToWfMethod = (apptNotifType) => {
  let wfMethod;
  switch (apptNotifType) {
    // CALENDAR_INVITE is represented in server template as method: 'email'
    case WF_TYPE.CALENDAR_INVITE:
    case WF_TYPE.APPT_EMAIL_FOLLOW_UP:
    case WF_TYPE.APPT_EMAIL_REMINDER:
    case WF_TYPE.CONFIRMATION:
    case WF_TYPE.CANCELLATION:
      wfMethod = WF_METHOD.EMAIL;
      break;
    case WF_TYPE.APPT_SMS_REMINDER:
      wfMethod = WF_METHOD.TEXT;
      break;
    default:
      console.error('unknown appt notification type', apptNotifType);
      wfMethod = WF_METHOD.EMAIL;
      break;
  }
  return wfMethod;
};

/**
 * Format the given localized message templates into the customNotifications format
 * @param {Object[]} localeTemplates
 * @return {Object}
 */
export const collectApptNotificationDefaults = (localeTemplates) => {
  const wfs = [
    ...SYSTEM_EMAIL_WORKFLOWS,
    ...SYSTEM_CALENDAR_WORKFLOWS,
    ...SYSTEM_INDIVIDUAL_WORKFLOWS,
  ];

  const results = {};
  wfs.forEach((wf) => {
    const res = {
      subject: '',
      body: '',
    };
    if (!wf.isSystem) {
      // add appt notification reminder fields
      res.enabled = wf.enabled;
      res.minutes = wf.minutes;
    }
    const wfActionMethodType = wf.email.length ? WF_METHOD.EMAIL : WF_METHOD.TEXT;
    const wfAction = wf[wfActionMethodType][0];
    const templates = localeTemplates;
    for (let i = 0; i < templates.length; i++) {
      const template = templates[i];
      if (template.receiver === wfAction.receiver && template.method === wfActionMethodType) {
        if (template.variant === wfAction.variant) {
          res.subject = template.subject;
          res.body = template.body;
          break;
        }
      }
    }
    results[wf.type] = res;
  });

  return {
    email: {
      attendeeConfirmation: results.confirmation,
      attendeeCancellation: results.cancellation,
    },
    calendar: {
      attendeeInvitation: results.calendarInvitation,
    },
    attendeeEmailReminder: results.attendeeEmailReminder,
    attendeeSmsReminder: results.attendeeSmsReminder,
    attendeeEmailFollowUp: results.attendeeEmailFollowUp,
  };
};

/**
 * Compare template contents and updates with newMsgTemplates if the contents have not been customized.
 * Sets warning field on notifications that were not updated for user feedback
 * @param {*} formContext
 * @param {[]} newMsgTemplates the templates in the new locale
 * @return {void}
 */
export const updateApptNotifications = (formContext, newMsgTemplates) => {
  const formValues = formContext.getValues();
  const prevTemplates = formValues.localeTemplates ?? [];
  // iterate appt notification templates
  const apptNotifs = [
    {
      ...SYSTEM_CALENDAR_WORKFLOWS[0],
      ...formValues.customNotifications.calendar.attendeeInvitation,
    },
    {
      ...SYSTEM_EMAIL_WORKFLOWS[0],
      ...formValues.customNotifications.email.attendeeConfirmation,
    },
    {
      ...SYSTEM_EMAIL_WORKFLOWS[1],
      ...formValues.customNotifications.email.attendeeCancellation,
    },
    ...SYSTEM_INDIVIDUAL_WORKFLOWS.map((wf) => ({
      ...wf,
      ...formValues.customNotifications[wf.type],
    })),
  ];
  apptNotifs.forEach((apptNotif) => {
    const wfType = customApptNotifTypeToWfVariant(apptNotif.type);
    const wfMethod = customApptNotifTypeToWfMethod(apptNotif.type);
    // find the original template for that "type"
    const newTemplate = newMsgTemplates.find((t) => t.variant === wfType &&
      t.receiver === WF_ACTION_RECEIVER.BOOKER && t.method === wfMethod);
    const newDefaultSubject = newTemplate?.subject ?? '';
    const newDefaultBody = newTemplate?.body ?? '';

    const prevTemplate = prevTemplates.find((t) => t.variant === wfType &&
      t.receiver === WF_ACTION_RECEIVER.BOOKER && t.method === wfMethod);

    const prevDefaultSubject = prevTemplate?.subject ?? '';
    const prevDefaultBody = prevTemplate?.body ?? '';

    const currSubject = apptNotif.subject ?? '';
    const currBody = apptNotif.body ?? '';
    // If the content is not matching the previous template, it was customized
    // - we should not auto apply the new template
    const customized = prevDefaultSubject !== currSubject || prevDefaultBody !== currBody;
    let applyValues;
    if (customized && apptNotif.enabled !== false) {
      applyValues = {
        ...(wfMethod !== WF_METHOD.TEXT) && {subject: apptNotif.subject},
        body: apptNotif.body,
        warning: 'notifForm.customizedContentNotUpdated',
      };
    } else {
      applyValues = {
        ...(wfMethod !== WF_METHOD.TEXT) && {subject: newDefaultSubject},
        body: newDefaultBody,
      };
    }
    if (apptNotif.type === WF_TYPE.CONFIRMATION) {
      formContext.setValue('customNotifications.email.attendeeConfirmation', {
        ...applyValues,
      });
    } else if (apptNotif.type === WF_TYPE.CANCELLATION) {
      formContext.setValue('customNotifications.email.attendeeCancellation', {
        ...applyValues,
      });
      // TODO: Handle calendar invites better.
    } else if (apptNotif.type === WF_TYPE.CALENDAR_INVITE || apptNotif.type === 'attendeeInvitation') {
      formContext.setValue('customNotifications.calendar.attendeeInvitation', {
        ...applyValues,
      });
    } else {
      const notifType = apptNotif.type;
      formContext.setValue(`customNotifications.${notifType}`, {
        enabled: apptNotif.enabled,
        trigger: apptNotif.trigger,
        ...applyValues,
      });
    }
  });
  // Track the new baseline templates to use for customization detection
  formContext.setValue('localeTemplates', newMsgTemplates);
};

/**
 * Attaches an customized flag depending on if the content differs from the template
 * @param {Object} customNotifications
 * @param {[]} localeTemplates
 * @return {Object}
 */
export const addCustomizedFlags = (customNotifications, localeTemplates) => {
  const currDefaultTemplates = localeTemplates ?? [];
  // iterate appt notification templates
  const apptNotifs = [
    {
      ...SYSTEM_CALENDAR_WORKFLOWS[0],
      ...customNotifications.calendar.attendeeInvitation,
    },
    {
      ...SYSTEM_EMAIL_WORKFLOWS[0],
      ...customNotifications.email.attendeeConfirmation,
    },
    {
      ...SYSTEM_EMAIL_WORKFLOWS[1],
      ...customNotifications.email.attendeeCancellation,
    },
    ...SYSTEM_INDIVIDUAL_WORKFLOWS.map((wf) => ({
      ...wf,
      ...customNotifications[wf.type],
    })),
  ];
  apptNotifs.forEach((apptNotif) => {
    const wfType = customApptNotifTypeToWfVariant(apptNotif.type);
    const wfMethod = customApptNotifTypeToWfMethod(apptNotif.type);
    // find the original template for that "type"
    const defaultTemplate = currDefaultTemplates.find(
      (t) =>
        t.variant === wfType &&
        t.receiver === WF_ACTION_RECEIVER.BOOKER &&
        t.method === wfMethod
    );

    const defaultSubject = defaultTemplate?.subject ?? '';
    const defaultBody = defaultTemplate?.body ?? '';

    const currSubject = apptNotif.subject ?? '';
    const currBody = apptNotif.body ?? '';
    const isCustomizedAndEnabled =
      (defaultSubject !== currSubject || defaultBody !== currBody) &&
      apptNotif.enabled !== false;

    // Attach the customized flag based on the default templates matching
    if (apptNotif.type === WF_TYPE.CONFIRMATION) {
      set(customNotifications, 'email.attendeeConfirmation.customized', isCustomizedAndEnabled);
    } else if (apptNotif.type === WF_TYPE.CANCELLATION) {
      set(customNotifications, 'email.attendeeCancellation.customized', isCustomizedAndEnabled);
    } else if (apptNotif.type === WF_TYPE.CALENDAR_INVITE || apptNotif.type === 'attendeeInvitation') {
      set(customNotifications, 'calendar.attendeeInvitation.customized', isCustomizedAndEnabled);
    } else {
      const notifType = apptNotif.type;
      set(customNotifications, `${notifType}.customized`, isCustomizedAndEnabled);
    }
  });
  return customNotifications;
};
